import React from "react";
import "./PasswordStrengthIndicator.css";

export default function PasswordStrengthIndicator({ password }) {
  function calculateStrength(pwd) {
    if (!pwd) return { score: 0, label: "", color: "" };

    let score = 0;
    const length = pwd.length;

    // Length scoring (0-35 points) - more emphasis on length
    if (length < 8) {
      score = 0; // Too short, start at 0
    } else if (length >= 8) {
      score += 10;
    }
    if (length >= 12) score += 10;
    if (length >= 16) score += 10;
    if (length >= 20) score += 5;

    // Character variety (0-40 points)
    const hasLower = /[a-z]/.test(pwd);
    const hasUpper = /[A-Z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSymbol = /[^a-zA-Z0-9]/.test(pwd);
    
    if (hasLower) score += 10;
    if (hasUpper) score += 10;
    if (hasNumber) score += 10;
    if (hasSymbol) score += 10;

    // Bonus for using all character types (15 points)
    if (hasLower && hasUpper && hasNumber && hasSymbol) {
      score += 15;
    }

    // Character diversity (0-20 points)
    const uniqueChars = new Set(pwd).size;
    const diversityRatio = uniqueChars / length;
    if (diversityRatio > 0.7) score += 20;
    else if (diversityRatio > 0.5) score += 10;
    else if (diversityRatio > 0.3) score += 5;

    // Penalties
    // Too short (less than 10 characters)
    if (length < 10) score -= 20;
    
    // Repeated characters (e.g., "aaa", "111")
    if (/(.)\1{2,}/.test(pwd)) score -= 20;
    
    // Sequential characters (e.g., "abc", "123")
    if (/abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789/i.test(pwd)) {
      score -= 15;
    }
    
    // Common words and patterns
    const commonPatterns = [
      'password', 'pass', 'qwerty', 'asdf', '1234', 'admin', 'letmein', 
      'welcome', 'monkey', 'dragon', 'master', 'login', 'test', 'user',
      'root', 'toor', 'changeme', 'secret', 'iloveyou'
    ];
    if (commonPatterns.some(pattern => pwd.toLowerCase().includes(pattern))) {
      score -= 30;
    }
    
    // Common substitutions (e.g., @ for a, 3 for e, 1 for i, 0 for o)
    // These are predictable and don't add much security
    const hasCommonSubstitution = /@/.test(pwd) || /[30!]/.test(pwd);
    if (hasCommonSubstitution && length < 12) {
      score -= 10; // Penalize short passwords with common substitutions
    }
    
    // Dictionary word detection (simple check for common patterns)
    // Check if password starts with capital letter followed by lowercase (e.g., "Test")
    if (/^[A-Z][a-z]+/.test(pwd) && length < 12) {
      score -= 15; // Likely a dictionary word
    }
    
    // Only one character type
    if (!hasLower && !hasUpper && hasNumber) score -= 25; // Only numbers
    if ((hasLower || hasUpper) && !hasNumber && !hasSymbol) score -= 20; // Only letters
    
    // Low character diversity
    if (uniqueChars < 6 && length >= 8) score -= 15;

    // Ensure score is between 0 and 100
    score = Math.max(0, Math.min(100, score));

    // Determine strength level based on score
    let strength;
    
    if (score >= 80) {
      strength = { score: 5, label: "Very Strong", color: "#28a745" };
    } else if (score >= 60) {
      strength = { score: 4, label: "Strong", color: "#5cb85c" };
    } else if (score >= 40) {
      strength = { score: 3, label: "Good", color: "#f0ad4e" };
    } else if (score >= 20) {
      strength = { score: 2, label: "Weak", color: "#ff8c00" };
    } else {
      strength = { score: 1, label: "Very Weak", color: "#d73a49" };
    }

    return strength;
  }

  const strength = calculateStrength(password);

  if (!password) return null;

  return (
    <div className="PasswordStrengthIndicator">
      <div className="strength-bar-container">
        <div 
          className="strength-bar" 
          style={{ 
            width: `${(strength.score / 5) * 100}%`,
            backgroundColor: strength.color
          }}
        />
      </div>
      <div className="strength-label" style={{ color: strength.color }}>
        Password Strength: <strong>{strength.label}</strong>
      </div>
    </div>
  );
}
